/**
 * Promedios de conceptos variables (≥3 meses distintos) por ventana de beneficio — liquidación.
 * Sustento: D.S. 001-97-TR art. 16; ventanas por beneficio según D.S. 005-2002-TR y D.L. 650.
 */

/** Una línea de sustento normativo por beneficio (referencia RC en liquidación). */
export const BASES_LEGALES_RC_LIQUIDACION = {
  vacaciones: 'Base legal RC: sueldo pactado vigente a la fecha de cese.',
  gratificacion:
    'Base legal RC: remuneración del mes calendario inmediato anterior al cese (D.S. 005-2002-TR art. 5.3).',
  cts: 'Base legal RC: sueldo pactado vigente a la fecha de cese.',
};

const CODIGO_SUELDO_BASICO = '0121';
const MIN_MESES_DISTINTOS = 3;

function normalizarCodigoPlameReg(cod) {
  const digits = String(cod ?? '').replace(/\D/g, '') || '0';
  return digits.padStart(4, '0');
}

function parseDateSafeLocal(value) {
  if (!value) return new Date(NaN);
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const soloFecha = value.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (soloFecha) {
      const [, y, m, d] = soloFecha;
      return new Date(Number(y), Number(m) - 1, Number(d));
    }
  }
  return new Date(value);
}

function mesesCalendarioEntre(desde, hasta) {
  const out = [];
  const c = new Date(desde.getFullYear(), desde.getMonth(), 1);
  const fin = new Date(hasta.getFullYear(), hasta.getMonth(), 1);
  while (c <= fin) {
    out.push(`${c.getFullYear()}-${String(c.getMonth() + 1).padStart(2, '0')}`);
    c.setMonth(c.getMonth() + 1);
  }
  return out;
}

function mesesEnInterseccionVinculo(fechaIngreso, fechaCese, semIni, semFin) {
  const ing = parseDateSafeLocal(fechaIngreso);
  const cese = parseDateSafeLocal(fechaCese);
  const desde =
    ing > semIni ? new Date(ing.getFullYear(), ing.getMonth(), 1) : new Date(semIni.getFullYear(), semIni.getMonth(), 1);
  const hasta =
    cese < semFin ? new Date(cese.getFullYear(), cese.getMonth(), 1) : new Date(semFin.getFullYear(), semFin.getMonth(), 1);
  if (desde > hasta) return [];
  return mesesCalendarioEntre(desde, hasta);
}

export function mesesVentanaGratificacion(fechaIngreso, fechaCese) {
  const cese = parseDateSafeLocal(fechaCese);
  const y = cese.getFullYear();
  const m = cese.getMonth() + 1;
  let semIni;
  let semFin;
  if (m >= 7) {
    semIni = new Date(y, 6, 1);
    semFin = new Date(y, 11, 31);
  } else {
    semIni = new Date(y, 0, 1);
    semFin = new Date(y, 5, 30);
  }
  return mesesEnInterseccionVinculo(fechaIngreso, fechaCese, semIni, semFin);
}

export function mesesVentanaCts(fechaIngreso, fechaCese) {
  const cese = parseDateSafeLocal(fechaCese);
  const y = cese.getFullYear();
  const m = cese.getMonth() + 1;
  let semIni;
  let semFin;
  if (m >= 5 && m <= 10) {
    semIni = new Date(y, 4, 1);
    semFin = new Date(y, 9, 31);
  } else if (m <= 4) {
    semIni = new Date(y - 1, 10, 1);
    semFin = new Date(y, 3, 30);
  } else {
    semIni = new Date(y, 10, 1);
    semFin = new Date(y + 1, 3, 30);
  }
  return mesesEnInterseccionVinculo(fechaIngreso, fechaCese, semIni, semFin);
}

export function mesesVentanaVacaciones(fechaIngreso, fechaCese) {
  return mesesVentanaCts(fechaIngreso, fechaCese);
}

function conceptoExcluidoPorDescripcion(desc) {
  const d = String(desc || '').toUpperCase();
  if (d.includes('GRATIFICACION') && (d.includes('FIESTAS PATRIAS') || d.includes('NAVIDAD'))) return true;
  if (d.includes('BONIFICACIÓN EXTRAORDINARIA') || d.includes('BONIFICACION EXTRAORDINARIA')) return true;
  if (d.includes('COMPENSACIÓN POR TIEMPO DE SERVICIOS') || d.includes('COMPENSACION POR TIEMPO DE SERVICIOS')) return true;
  return false;
}

function conceptoExcluidoPorCodigo(cod) {
  return ['0406', '0313', '0904', '2010'].includes(normalizarCodigoPlameReg(cod));
}

function incluirEnRc(cod, desc) {
  if (conceptoExcluidoPorCodigo(cod)) return false;
  if (conceptoExcluidoPorDescripcion(desc)) return false;
  return true;
}

/**
 * @param {object} incluidosMap clave mesKey:idx → boolean (false = excluido)
 */
export function sumaPromediosConceptosRegulares(remPorMesList, mesesVentana, incluidosMap = null) {
  if (!mesesVentana?.length) return { total: 0, conceptos: [] };
  const ventanaSet = new Set(mesesVentana);
  const porCodigoMes = {};

  (remPorMesList || []).forEach((row) => {
    const mk = row.mes_key || row.mes;
    if (!mk || !ventanaSet.has(mk)) return;
    (row.conceptos || []).forEach((c, idx) => {
      if (c.incluir === false) return;
      const id = `${mk}:${idx}`;
      if (incluidosMap && incluidosMap[id] === false) return;
      const cod = normalizarCodigoPlameReg(c.codigo_plame);
      if (cod === CODIGO_SUELDO_BASICO) return;
      const nombre = c.nombre || '';
      if (!incluirEnRc(cod, nombre)) return;
      const monto = Number(c.monto) || 0;
      if (monto <= 0) return;
      if (!porCodigoMes[cod]) porCodigoMes[cod] = {};
      porCodigoMes[cod][mk] = { monto, nombre };
    });
  });

  const conceptos = [];
  let total = 0;
  Object.entries(porCodigoMes).forEach(([cod, porMes]) => {
    const meses = Object.keys(porMes);
    if (meses.length < MIN_MESES_DISTINTOS) return;
    const montos = meses.map((k) => porMes[k].monto);
    const promedio = Math.round((montos.reduce((a, b) => a + b, 0) / montos.length) * 100) / 100;
    total += promedio;
    conceptos.push({
      codigo_plame: cod,
      nombre: porMes[meses[0]].nombre || cod,
      meses: meses.length,
      promedio,
    });
  });

  return { total: Math.round(total * 100) / 100, conceptos };
}

export function calcularReferenciasRemuneracionLiquidacion(detalle, remPorMesList, incluidosMap, sueldoBasico) {
  const ingreso = detalle?.fecha_ingreso;
  const cese = detalle?.fecha_cese;
  const basico = Number(sueldoBasico) || 0;
  const promVac = sumaPromediosConceptosRegulares(remPorMesList, mesesVentanaVacaciones(ingreso, cese), incluidosMap);
  const promGrat = sumaPromediosConceptosRegulares(remPorMesList, mesesVentanaGratificacion(ingreso, cese), incluidosMap);
  const promCts = sumaPromediosConceptosRegulares(remPorMesList, mesesVentanaCts(ingreso, cese), incluidosMap);
  const base0121Grat = remuneracionGrat0121FromDetalle(detalle, remPorMesList, basico);

  return {
    sueldo_basico: basico,
    vacaciones: {
      ...promVac,
      meses_ventana: mesesVentanaVacaciones(ingreso, cese),
      remuneracion_total: Math.round((basico + promVac.total) * 100) / 100,
    },
    gratificacion: {
      ...promGrat,
      meses_ventana: mesesVentanaGratificacion(ingreso, cese),
      base_0121_mes_anterior: base0121Grat,
      remuneracion_total: Math.round((base0121Grat + promGrat.total) * 100) / 100,
    },
    cts: {
      ...promCts,
      meses_ventana: mesesVentanaCts(ingreso, cese),
      remuneracion_total: Math.round((basico + promCts.total) * 100) / 100,
    },
  };
}

function remuneracionGrat0121FromDetalle(detalle, remPorMesList, fallback) {
  const info = detalle?.remuneracion_gratificacion;
  if (info?.monto != null && Number(info.monto) > 0) return Number(info.monto);
  const cese = detalle?.fecha_cese;
  if (!cese) return fallback;
  const d = parseDateSafeLocal(cese);
  const prev = new Date(d.getFullYear(), d.getMonth() - 1, 1);
  const key = `${prev.getFullYear()}-${String(prev.getMonth() + 1).padStart(2, '0')}`;
  const fila = (remPorMesList || []).find((m) => (m.mes_key || m.mes) === key);
  const c0121 = (fila?.conceptos || []).find(
    (c) => normalizarCodigoPlameReg(c.codigo_plame) === CODIGO_SUELDO_BASICO
  );
  const m = c0121?.monto;
  return m != null && Number(m) > 0 ? Number(m) : fallback;
}
